/**
 * Utility functions for managing cache and handling authentication-related cache issues
 */

/**
 * Clears service worker cache for API requests
 */
export const clearAPICache = async (): Promise<void> => {
    // Try to clear via service worker
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        console.log('Clearing API cache via service worker');
        navigator.serviceWorker.controller.postMessage({
            type: 'CLEAR_API_CACHE'
        });
    }

    // Also try to clear browser cache for API requests
    if ('caches' in window) {
        try {
            const cacheNames = await caches.keys();

            await Promise.all(
                cacheNames.map(async (cacheName) => {
                    const cache = await caches.open(cacheName);
                    const requests = await cache.keys();

                    return Promise.all(
                        requests
                            .filter(request => {
                                const url = new URL(request.url);
                                return url.pathname.startsWith('/api/') ||
                                    url.href.includes('getUserDetails') ||
                                    url.pathname.includes('/users/');
                            })
                            .map(request => cache.delete(request))
                    );
                })
            );

            console.log('API cache cleared successfully');
        } catch (error) {
            console.error('Error clearing API cache:', error);
        }
    }
};

/**
 * Completely clears all service worker caches
 */
export const clearAllCaches = async (): Promise<void> => {
    // Try to clear via service worker
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        console.log('Clearing all caches via service worker');
        navigator.serviceWorker.controller.postMessage({
            type: 'CLEAR_CACHE'
        });
    }

    // Also try to clear all browser caches
    if ('caches' in window) {
        try {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
            console.log('All caches cleared successfully');
        } catch (error) {
            console.error('Error clearing caches:', error);
        }
    }
};

/**
 * Forces a hard reload of the application and clears caches
 * Used for authentication issues where we need a fresh start
 */
export const forceReloadAndClearCache = async (): Promise<void> => {
    try {
        await clearAllCaches();

        // Use window.location.reload(true) for a hard reload
        // The true parameter forces a reload from the server, not from cache
        window.location.href = '/login?forceClear=true';

        // If location.href doesn't trigger, fall back to reload
        setTimeout(() => {
            window.location.reload();
        }, 500);
    } catch (error) {
        console.error('Error during force reload:', error);
        // Last resort - just reload
        window.location.reload();
    }
}; 